import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  Globe2,
  Zap,
  Lock,
  Percent,
  Building,
  UserCheck,
} from "lucide-react";
import { useConsultation } from "../../../context/ConsultationContext";

const IMG =
  "https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=1600&q=80";

const BENEFITS = [
  {
    icon: UserCheck,
    title: "100% Foreign Ownership",
    copy: "Own your business outright in most mainland and all freezone sectors — no local sponsor required.",
  },
  {
    icon: Globe2,
    title: "Access to Global Markets",
    copy: "The UAE's strategic location connects your business to Europe, Asia and Africa within a 4-hour flight radius.",
  },
  {
    icon: Zap,
    title: "Fast Company Formation",
    copy: "Get your trade license and company registered in as little as 3–7 business days in UAE freezones.",
  },
  {
    icon: Lock,
    title: "Full Privacy & Confidentiality",
    copy: "UAE law provides strong legal protections for your business information, assets and personal data.",
  },
  {
    icon: Percent,
    title: "Tax-Friendly Environment",
    copy: "0% personal income tax and 0% corporate tax for qualifying free zone entities in the UAE.",
  },
  {
    icon: Building,
    title: "No Physical Presence Needed",
    copy: "Register your UAE company remotely — without needing to be physically present in the country.",
  },
];

export default function WhyUAEV2() {
  const { open } = useConsultation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  return (
    <section
      ref={ref}
      data-testid="v2-why-uae"
      className="relative bg-white py-10 md:py-14 overflow-hidden"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left: sticky image with parallax */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-sm overflow-hidden"
            >
              <motion.img
                style={{ y }}
                src={IMG}
                alt="Dubai aerial view"
                className="w-full h-[340px] md:h-[420px] object-cover scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#002366]/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="overline text-white/70">Why founders choose UAE</p>
                <h3 className="mt-3 font-heading font-light text-white text-2xl md:text-3xl leading-tight">
                  <span className="font-black text-[#6B8AFF]">#1</span> business-friendly destination in MEA.
                </h3>
              </div>
            </motion.div>

            <button
              data-testid="v2-why-cta"
              onClick={() => open("other")}
              className="mt-6 btn-gold w-full"
            >
              Get In Touch
            </button>
          </div>

          {/* Right: benefits grid */}
          <div className="lg:col-span-7">
            <p className="overline text-[#3366FF]">Why set up in the UAE</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
              One of the world's most{" "}
              <span className="font-black text-[#002366]">business-friendly</span> destinations.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">
              Zero personal income tax, 100% foreign ownership and fast company
              registration — structured to let founders move from idea to invoicing
              in weeks, not months.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {BENEFITS.map(({ icon: Icon, title, copy }, i) => (
                <motion.div
                  key={title}
                  data-testid={`v2-benefit-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  className="p-6 rounded-sm border border-slate-200 bg-white hover:bg-[#F8FAFC] hover:border-[#3366FF]/40 hover:shadow-md transition-all group"
                >
                  <div className="w-10 h-10 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center group-hover:bg-[#002366] group-hover:text-[#6B8AFF] transition-colors">
                    <Icon className="w-4 h-4" />
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-[#0F172A] tracking-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {copy}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
